import {
  AnalyticsBrowser,
  AnalyticsBrowserSettings,
  InitOptions,
} from '@segment/analytics-next';
import { PatientGender } from '@types';
import {
  baseProperties,
  Events,
  EventProperties,
  ExperimentNames,
  BaseProperties,
  QuizSessionProperties,
} from './events';
import { getPageNameFromPath } from './utils';
import { captureException } from '@sentry/react';

export type QuizViewProperties = QuizSessionProperties & {
  screen_name: string;
  questionType: 'single_select' | 'multi_select';
};
export type QuizAnswerProperties = QuizSessionProperties & {
  screen_name: string;
  answer_value: string[];
  duration_seconds: number;
  questionType: 'single_select' | 'multi_select';
  selectedOptionIds: string[];
};

const browserSettings: AnalyticsBrowserSettings = {
  cdnURL: 'https://analytics-cdn.quick.md',
  writeKey: import.meta.env.VITE_SEGMENT_WRITE_KEY,
};

const initOptions: InitOptions = {
  integrations: {
    'Segment.io': {
      apiHost: 'analytics.quick.md/v1',
      protocol: 'https',
    },
  },
};
type UserParams = {
  gender?: PatientGender;
  hasVisits?: boolean;
  mrn?: string;
  patientAddressStateCode?: string;
  email?: string;
};

type IdentifyUserParams = {
  userId: string | undefined;
  properties: UserParams;
};

type TrackPageParams = {
  pageName?: string;
  pathname?: string;
};

type ExperimentProperties = {
  experimentName: ExperimentNames;
  variantName: string;
};

export class Tracking {
  analytics: AnalyticsBrowser | undefined;
  baseProperties: BaseProperties = {
    ...baseProperties,
  };
  experimentProperties: ExperimentProperties | undefined;

  constructor(analytics: AnalyticsBrowser | undefined) {
    this.analytics = analytics;
  }

  setBaseProperty(property: Partial<BaseProperties>) {
    this.baseProperties = {
      ...this.baseProperties,
      ...property,
    };
  }

  private _fire(eventName: string, properties?: Record<string, unknown> | null) {
    try {
      this.analytics?.track(eventName, {
        ...(properties ?? {}),
        ...this.baseProperties,
        ...this.experimentProperties,
      });
    } catch (error) {
      captureException(error);
    }
  }

  trackEvent<T extends Events>(event: T, properties?: EventProperties[T]) {
    this._fire(event, properties as Record<string, unknown> | null);
  }

  trackQuizViewEvent(eventName: string, properties: QuizViewProperties) {
    this._fire(eventName, properties);
  }

  trackQuizAnswerEvent(eventName: string, properties: QuizAnswerProperties) {
    this._fire(eventName, properties);
  }

  trackExperimentStarted(properties: ExperimentProperties) {
    this.trackEvent(Events.EXPERIMENT_STARTED, properties);

    this.experimentProperties = properties;
  }

  trackPage({ pageName, pathname }: TrackPageParams) {
    let name;

    if (pageName) {
      name = pageName;
    } else if (pathname) {
      name = getPageNameFromPath(pathname);
    }

    try {
      this.analytics?.page(name, {
        ...this.baseProperties,
        ...this.experimentProperties,
      });
    } catch (error) {
      captureException(error);
    }
  }

  identifyUser(params: IdentifyUserParams) {
    try {
      this.analytics?.identify(params.userId, params.properties);
    } catch (error) {
      captureException(error);
    }
  }

  reset() {
    try {
      this.analytics?.reset();
      this.experimentProperties = undefined;
      this.baseProperties = { ...baseProperties };
    } catch (error) {
      captureException(error);
    }
  }
}

const PII_FIELDS = ['firstName', 'lastName', 'email', 'phone', 'patientId', 'mrn'] as const;

const analytics = AnalyticsBrowser.load(browserSettings, initOptions);

// Strip PII from browser-side ad destinations. Segment's destination filters are
// server-side only and don't apply to device-mode (client-side) destinations like
// Google Ads Gtag, which fire conversion pixels with raw event properties in the browser.
analytics.addDestinationMiddleware('Google AdWords New', ({ payload, next }) => {
  if (payload.obj.type === 'track' && payload.obj.properties) {
    const props = { ...payload.obj.properties } as Record<string, unknown>;
    for (const field of PII_FIELDS) {
      delete props[field];
    }
    payload.obj.properties = props;
  }
  next(payload);
});

const tracking = new Tracking(analytics);

export { Events, getPageNameFromPath, tracking };
